/* ============================================================
 * MakeMyMask : template d'article dermo-capillaire
 * ------------------------------------------------------------
 * 1. Mesure la hauteur du header sticky et l'expose en CSS var
 * 2. Nettoie le HTML collé depuis ChatGPT (blocs vides)
 * 3. Rend les tableaux scrollables horizontalement
 * 4. Génère le sommaire depuis les H2 du corps
 * 5. Scrollspy + scroll doux avec offset header
 *
 * Note : les regex sur l'espace insécable et les diacritiques
 * sont construites via new RegExp pour éviter tout caractère
 * invisible dans le source.
 * ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-bnz-article]');
  if (!root) return;

  var content = root.querySelector('.bnz-article__content');
  var toc = root.querySelector('.bnz-article__toc');
  var tocList = toc ? toc.querySelector('.bnz-article__toc-list') : null;

  var NBSP = new RegExp('[\\u00a0\\u202f\\u2007]', 'g');
  var DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

  function normText(value) {
    return (value || '').replace(NBSP, ' ').trim();
  }

  function absoluteTop(el) {
    return el.getBoundingClientRect().top + window.pageYOffset;
  }

  /* --------------------------------------------------------
   * 1. Offset du header sticky
   * -------------------------------------------------------- */
  function measureHeaderOffset() {
    var offset = 0;
    var candidates = document.querySelectorAll(
      'header, .header, .site-header, .shopify-section-header, [data-sticky], sticky-header'
    );

    Array.prototype.forEach.call(candidates, function (el) {
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return;
      if (el.getBoundingClientRect().top > 1) return;
      offset = Math.max(offset, el.offsetHeight);
    });

    document.documentElement.style.setProperty('--bnz-art-header-offset', offset + 'px');
    return offset;
  }

  var headerOffset = measureHeaderOffset();
  window.addEventListener(
    'resize',
    function () {
      headerOffset = measureHeaderOffset();
    },
    { passive: true }
  );

  /* --------------------------------------------------------
   * 2. Nettoyage du HTML collé
   * Un paragraphe ne contenant qu'un espace insécable, ou un
   * titre ne contenant qu'un <br>, n'est pas :empty en CSS.
   * On les marque ici pour que le CSS les masque.
   * -------------------------------------------------------- */
  function cleanPastedMarkup() {
    if (!content) return;

    var blocks = content.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li');
    Array.prototype.forEach.call(blocks, function (el) {
      // On garde tout bloc porteur de média ou de structure
      if (el.querySelector('img, iframe, video, picture, svg, table, input, button')) return;
      if (normText(el.textContent) === '') el.classList.add('bnz-is-blank');
    });
  }

  /* --------------------------------------------------------
   * 3. Tableaux scrollables (la page ne doit jamais scroller
   *    horizontalement à cause d'un tableau large)
   * -------------------------------------------------------- */
  function wrapTables() {
    if (!content) return;

    Array.prototype.forEach.call(content.querySelectorAll('table'), function (table) {
      var parent = table.parentNode;
      if (!parent) return;
      if (parent.classList && parent.classList.contains('bnz-article__table-scroll')) return;

      var wrap = document.createElement('div');
      wrap.className = 'bnz-article__table-scroll';
      parent.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* --------------------------------------------------------
   * 4. Sommaire
   * -------------------------------------------------------- */
  function slugify(text, index) {
    var slug = (text || '')
      .normalize('NFD')
      .replace(DIACRITICS, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);

    return slug ? 'sec-' + slug : 'sec-' + (index + 1);
  }

  var headings = [];

  function buildToc() {
    if (!content || !tocList) return;

    var found = Array.prototype.filter.call(content.querySelectorAll('h2'), function (h) {
      if (h.classList.contains('bnz-is-blank')) return false;
      return normText(h.textContent) !== '';
    });

    // Un sommaire d'un seul item n'apporte rien
    if (found.length < 2) {
      if (toc) toc.hidden = true;
      return;
    }

    var fragment = document.createDocumentFragment();
    var used = {};

    found.forEach(function (heading, index) {
      var label = normText(heading.textContent);
      var id = heading.id || slugify(label, index);

      // Un slug tronqué peut collisionner, on suffixe si besoin
      if (used[id]) id = id + '-' + (index + 1);
      used[id] = true;
      heading.id = id;

      var li = document.createElement('li');
      var link = document.createElement('a');
      link.href = '#' + id;
      link.textContent = label;
      li.appendChild(link);
      fragment.appendChild(li);

      headings.push({ el: heading, link: link });
    });

    tocList.appendChild(fragment);
    if (toc) toc.hidden = false;
  }

  /* --------------------------------------------------------
   * 5. Scroll doux + scrollspy
   * -------------------------------------------------------- */
  function scrollToHeading(target) {
    var y = absoluteTop(target) - headerOffset - 16;
    window.scrollTo({ top: y < 0 ? 0 : y, behavior: 'smooth' });
  }

  function bindTocClicks() {
    if (!tocList) return;

    tocList.addEventListener('click', function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      var link = node.closest('a[href^="#"]');
      if (!link) return;

      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;

      event.preventDefault();
      scrollToHeading(target);
      if (history.replaceState) history.replaceState(null, '', link.getAttribute('href'));
    });
  }

  function bindScrollSpy() {
    if (!headings.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var line = window.pageYOffset + headerOffset + 80;
      var activeIndex = 0;

      for (var i = 0; i < headings.length; i++) {
        if (absoluteTop(headings[i].el) <= line) activeIndex = i;
      }

      headings.forEach(function (item, i) {
        item.link.classList.toggle('is-active', i === activeIndex);
      });
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  cleanPastedMarkup();
  wrapTables();
  buildToc();
  bindTocClicks();
  bindScrollSpy();

  // Une ancre déjà présente dans l'URL doit tenir compte du header
  if (window.location.hash.length > 1) {
    var initial = document.getElementById(window.location.hash.slice(1));
    if (initial) {
      window.setTimeout(function () {
        scrollToHeading(initial);
      }, 60);
    }
  }
})();
